import { useEffect, useMemo } from 'react';
import type { FC } from 'react';

// Components
import Scrollbar from '../../components/Scrollbar';
import NavSection from '../common/NavSection';

// Externals
import { Box, Button, Divider, Drawer, Stack, SwipeableDrawer, useMediaQuery } from '@mui/material';
import type { Theme } from '@mui/material';
import { Launch } from '@mui/icons-material';
import PropTypes from 'prop-types';
import { useLocation, useParams } from 'react-router-dom';
import { dashboardUrl } from 'src/config';
import ShopAutocomplete from 'src/components/ShopAutocomplete';

interface SidebarProps {
  onMobileClose: () => void;
  onMobileOpen: () => void;
  openMobile: boolean;
}

const Sidebar: FC<SidebarProps> = ({ onMobileClose, onMobileOpen, openMobile }) => {
  const location = useLocation();
  const lgUp = useMediaQuery((theme: Theme) => theme.breakpoints.up('lg'));
  const { shopId } = useParams<{ shopId: string }>();

  const items = useMemo(
    () => [
      {
        title: 'Tableau de bord',
        path: `/${shopId}/overview`
      },
      {
        title: 'Décomptes',
        path: `/${shopId}/statements`
      },
      {
        title: 'Factures',
        path: `/${shopId}/invoices`
      }
    ],
    [shopId]
  );

  useEffect(() => {
    if (openMobile && onMobileClose) {
      onMobileClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const content = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%'
      }}
    >
      <Scrollbar options={{ suppressScrollX: true }}>
        <Box sx={{ mt: { xs: 2, lg: 8 }, px: 2 }}>
          <Stack sx={{ gap: 3, mb: 3 }}>
            <ShopAutocomplete />
            <Divider sx={{ backgroundColor: 'background.default' }} />
          </Stack>
          <NavSection items={items} pathname={location.pathname} />
        </Box>
      </Scrollbar>
      <Box flexGrow={1} />
      <Box
        sx={{
          display: 'flex',
          my: { xs: 2, lg: 8 },
          px: 2,
          py: 0.5
        }}
      >
        <Button
          component="a"
          href={`${dashboardUrl}/${shopId}/menu`}
          sx={{
            color: '#ffffff',
            fontSize: '1.5rem',
            justifyContent: 'flex-start',
            lineHeight: '2.5rem',
            pl: 3,
            paddingRight: '0px !important',
            textAlign: 'left',
            textTransform: 'none',
            width: '100%'
          }}
          variant="text"
        >
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              flexGrow: 1,
              flexDirection: 'row'
            }}
          >
            Mon commerce <Launch sx={{ ml: 1 }} />
          </Box>
        </Button>
      </Box>
    </Box>
  );

  if (lgUp) {
    return (
      <Drawer
        PaperProps={{
          sx: {
            backgroundColor: 'primary.main',
            width: 280
          }
        }}
        anchor="left"
        elevation={0}
        open={true}
        variant="permanent"
      >
        {content}
      </Drawer>
    );
  }

  return (
    <SwipeableDrawer
      PaperProps={{
        sx: {
          backgroundColor: 'primary.main',
          width: 280
        }
      }}
      anchor="left"
      elevation={0}
      onClose={onMobileClose}
      onOpen={onMobileOpen}
      open={openMobile}
      variant="temporary"
    >
      {content}
    </SwipeableDrawer>
  );
};

Sidebar.propTypes = {
  onMobileClose: PropTypes.func,
  onMobileOpen: PropTypes.func,
  openMobile: PropTypes.bool
};

export default Sidebar;
